import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { Helmet } from "react-helmet";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Container, Modal, Button, Card, Breadcrumb, Row, Col, Badge } from 'react-bootstrap'
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Services } from '../../api/Services';
import AlidaLoader from '../../components/common/AlidaLoader';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { useToasts } from '../../contexts/toasts';
import { getPropertyIcon } from '../../utils';
import UserItem from '../../components/common/UserItem';
import { useAuth } from '../../contexts/auth';
import ScriptEditor from '../../components/common/ScriptEditor';
import { BdaService, BdaServiceProperty } from '../../models/BdaService';
import { Flex, Space, Tag, Tooltip } from 'antd';
import AssetProjectsEditor from '../../components/forms/AssetProjectsEditor';
import MDEditor from '@uiw/react-md-editor';
import { useSelector } from 'react-redux';
import { sectionLabelsSelector } from '../../slices/sectionsSlice';
import { RootState } from '../../store';
import AppTag from '../../components/common/AppTag';
import EntityDefinitionModal from '../../components/generics/EntityDefinitionModal';
import AssetCategory from '../../components/common/AssetCategory';

export default function ServiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();

  const [service, setService] = useState<BdaService>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [showDefinitionModal, setShowDefinitionModal] = useState(false);
  const [property, setProperty] = useState<BdaServiceProperty>();
  const [error, setError] = useState(false);
  const [showProjectsModal, setShowProjectsModal] = useState(false);

  const serviceLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "services"));

  const { loggedUser } = useAuth();

  const serviceUrl: string = useMemo(() => {
    if (service) {
      const prop = service!.properties.find(prop => prop.key === "spark.kubernetes.container.image");
      return prop?.value || prop?.defaultValue || service!.url;
    }
  }, [service]);

  useEffect(() => {
    Services.getService(+id!)
      .then(
        ({ data }) => {
          Services.getServiceMarkDown(+id!)
            .then(
              ({ data: markdown }) => setService({ ...data, markDown: { value: markdown } }),
              () => setService(data),
            )
        },
        () => {
          showToast('service-error', `Could not get ${serviceLabels.singular}.`, `${serviceLabels.singular} Error`, 'danger');
          setError(true);
        }
      );
    // eslint-disable-next-line
  }, [id]);

  const handleServiceEdit = (clone?: boolean) => () => navigate(`/services/${id}/edit` + (clone ? '?mode=clone' : ''));
  const handleDeleteService = () => setShowDeleteModal(true);
  const handleDeleteClose = () => setShowDeleteModal(false);
  const handlePropertyClose = () => setShowPropertyModal(false);

  const deleteService = () => {
    showLoader();

    Services.deleteService(service!.id!)
      .then(
        () => {
          showToast('service-delete-success', `${serviceLabels.singular} deleted successfully.`, 'Success', 'success');
          hideLoader();
          handleDeleteClose();
          navigate('/services');
        },
        () => {
          showToast('service-delete-error', `Could not delete ${serviceLabels.singular}.`, `${serviceLabels.singular} Error`, 'danger');
          hideLoader();
        }
      );
  };

  const openPropertyModal = (prop: BdaServiceProperty) => () => {
    setProperty(prop);
    setShowPropertyModal(true);
  }

  const openProjectsModal = () => setShowProjectsModal(true);
  const openDefinitionModal = () => setShowDefinitionModal(true);
  const handleProjectsModalClose = () => setShowProjectsModal(false);
  const handleDefinitionModalClose = () => setShowDefinitionModal(false);

  const copyToClipboard = useCallback((value: string) => () => {
    navigator.clipboard.writeText(value)
      .then(() => showToast('copy-success', 'URL copied to clipboard!'));
  }, [showToast]);

  const renderPropertyValue = useCallback((property: BdaServiceProperty) => {
    const value = service!.createdByServiceId ? property.value : property.defaultValue;
    return (
      <Tooltip title={value} mouseEnterDelay={1}>
        <div className="line-clamp-2 overflow-hidden px-2">
          <code className="font-xl">{value}</code>
        </div>
      </Tooltip>
    )
  }, [service]);

  const renderAsyncContent = () => {
    if (error) return (
      <Alert variant="danger">
        <FontAwesomeIcon icon={solid('exclamation-triangle')} />
        <span className="ms-2">An error occurred during {serviceLabels.singular} retrieve. Try again later.</span>
      </Alert>
    );

    return !service
      ? (
        <div className="d-flex justify-content-center py-5">
          <AlidaLoader size={300} />
        </div>
      )
      : (
        <>
          <Breadcrumb>
            <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/services' }}>{serviceLabels.plural}</Breadcrumb.Item>
            <Breadcrumb.Item active>{service.name}</Breadcrumb.Item>
          </Breadcrumb>

          <Card className="item-detail-group main bg-service">
            <Flex align="center">
              {service.category && <AssetCategory className="shadow" category={service.category} solid />}
              <Tag color="blue" className="ms-auto shadow text-uppercase font-xs fw-bold m-0">{service.accessLevel}</Tag>
              {service.deleteDateTime && (<Tag color="red" className="shadow text-uppercase font-xs fw-bold m-0 ms-2">Deprecated</Tag>)}
            </Flex>

            {/* <FontAwesomeIcon className="item-icon" icon={iconsMap.get(service.area!) || solid('chart-pie')} /> */}

            <div className="position-relative p-2">
              <Row className="gx-0 align-items-center justify-content-md-between">
                <Col xs={12} md="auto" className="pe-3">
                  <h3 className="mb-0">{service.name}</h3>
                  <p className="text-trailing mb-3">{service.description}</p>
                </Col>

                <Col xs={12} md="auto" className="text-center text-md-start mb-2 mb-md-0">
                  <Tooltip title="Show Definition">
                    <button className="circle-button" onClick={openDefinitionModal}>
                      <FontAwesomeIcon icon={solid('code')} />
                    </button>
                  </Tooltip>
                  {(service.owner!.id === loggedUser!.sub && !service.deleteDateTime) && (
                    <>
                      <Tooltip title="Manage Projects">
                        <button className="circle-button" onClick={openProjectsModal}>
                          <span className="fa-layers">
                            <FontAwesomeIcon icon={solid('folder')} transform="left-14 grow-2" />
                            <FontAwesomeIcon icon={solid('cog')} transform="shrink-4 down-8 right-3" />
                          </span>
                        </button>
                      </Tooltip>
                      <Tooltip title="Clone">
                        <button className="circle-button" onClick={handleServiceEdit(true)}>
                          <FontAwesomeIcon icon={solid('clone')} />
                        </button>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <button className="circle-button" onClick={handleServiceEdit()}>
                          <FontAwesomeIcon icon={solid('pen')} />
                        </button>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <button className="circle-button" onClick={handleDeleteService}>
                          <FontAwesomeIcon icon={solid('trash')} />
                        </button>
                      </Tooltip>
                    </>
                  )}
                </Col>
              </Row>
            </div>

            <Flex className="mb-2">
              <div className="bg-highlight">
                <UserItem className="negative" user={service.owner!} organization={service.organization!} />
              </div>
            </Flex>

            {service.tags.length > 0 && (
              <Flex className="tags-wrapper w-auto mb-2">
                {service.tags.map(tag => <AppTag className="me-1" tag={tag} key={tag} />)}
              </Flex>
            )}

            <Flex align="end" justify="space-between">
              <Space size="small" className="tags-wrapper">
                <Tooltip title={`${serviceLabels.singular} Url`}>
                  <Badge className="service-version border-0 action" bg="none" onClick={copyToClipboard(serviceUrl)}>
                    <FontAwesomeIcon icon={solid('link')} />
                    {/* <b className="ms-2">{serviceLabels.singular} Url</b> */}
                    <span className="font-monospace ms-2">{service.url}</span>
                  </Badge>
                </Tooltip>
                {service.repoGitUrl && (
                  <Tooltip title="Git Url">
                    <Badge className="service-version border-0 action" bg="none" onClick={copyToClipboard(service.repoGitUrl)}>
                      <FontAwesomeIcon icon={solid('code-branch')} />
                      {/* <b className="ms-2">Git Url</b> */}
                      <span className="font-monospace ms-2">{service.repoGitUrl}</span>
                    </Badge>
                  </Tooltip>
                )}
              </Space>
              <Badge className="service-version" bg="none">{service.version}</Badge>
            </Flex>
          </Card>

          <h6 className="text-muted">{serviceLabels.singular} Properties</h6>

          {!service.properties.length && (
            <Alert className="py-2 mb-0">
              <FontAwesomeIcon icon={solid('info-circle')} />
              <span className="ms-3">This {serviceLabels.singular} has no properties.</span>
            </Alert>
          )}

          {service.properties.length > 0 && (
            <Row xs={1} md={2} xl={4} className="g-2">
              {service.properties.map(property => (
                <Col key={`prop-${property.key}`}>
                  <Card className="h-100 pb-3">
                    <Flex align="center" className="p-1">
                      <Badge bg="secondary" className="me-auto">
                        <FontAwesomeIcon icon={getPropertyIcon(property.type)} />
                        <span className="ms-2">{property.type}</span>
                      </Badge>
                      {property.valueType && <Badge>{property.valueType}</Badge>}
                      {property.mandatory && <Badge bg="danger" className="ms-1">Mandatory</Badge>}
                    </Flex>
                    <Card.Body>
                      <Flex justify="center" align="center">
                        <h5 className="font-monospace text-truncate mb-0">{property.key}</h5>
                        {property.description && (
                          <Tooltip title={
                            <>
                              <h6 className="mb-0">{property.key}</h6>
                              <span>{property.description}</span>
                            </>
                          }>
                            <FontAwesomeIcon icon={solid('info-circle')} className="ms-2 text-muted" />
                          </Tooltip>
                        )}
                      </Flex>
                    </Card.Body>
                    <div className="text-center">
                      {property.valueType === "JSON"
                        ? (
                          <Button size="sm" variant="light" className="border" onClick={openPropertyModal(property)}>
                            <FontAwesomeIcon icon={solid('eye')} />
                            <span className="ms-2">Show Value</span>
                          </Button>
                        )
                        : renderPropertyValue(property)
                      }
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          )}

          {service.markDown?.value && (
            <Card className="p-2 ps-3 mt-3">
              <div data-color-mode="light">
                <MDEditor.Markdown className="px-2" source={service.markDown.value} />
              </div>
            </Card>
          )}
        </>
      );
  }

  return (
    <Container className="mt-3">
      <Helmet>
        <title>{serviceLabels.singular} {id} Detail</title>
      </Helmet>

      {renderAsyncContent()}

      <br />

      <Modal show={showPropertyModal} onHide={handlePropertyClose}>
        <Modal.Header closeButton>
          <Modal.Title>Property '{property?.key}' {!service?.createdByServiceId && 'Default'} value</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{property?.description}</p>

          <ScriptEditor value={service?.createdByServiceId ? property?.value : property?.defaultValue} readOnly height={350} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handlePropertyClose}>Close</Button>
        </Modal.Footer>
      </Modal>

      <EntityDefinitionModal open={showDefinitionModal} onClose={handleDefinitionModalClose} entity={service} label={serviceLabels.singular} />

      <Modal show={showDeleteModal} onHide={handleDeleteClose}>
        <Modal.Header closeButton>
          <Modal.Title>Delete {serviceLabels.singular}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete {serviceLabels.singular} <b>{service && service.name}</b>?</p>

          <Alert className="p-2" variant="warning">
            <FontAwesomeIcon icon={solid('exclamation-triangle')} />
            <span className="ms-2">Remember, this operation cannot be undone.</span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handleDeleteClose}>Close</Button>
          <Button variant="danger" size="sm" onClick={deleteService}>Delete</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showProjectsModal} onHide={handleProjectsModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>{serviceLabels.singular} Projects</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <AssetProjectsEditor assetId={+id!} assetType="service" assetLabel={serviceLabels.singular} />
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
          <Button variant="light" className="border" onClick={handleProjectsModalClose} size="sm">Cancel</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}
